import { Link } from 'react-router'
import { Button } from '@klear/ui/components/button'
import { Card, CardContent } from '@klear/ui/components/card'
import { Loader2, RefreshCw, Download, Pencil, Columns3, ArrowLeft } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@klear/ui/components/dropdown-menu'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@klear/ui/components/select'
import { KlearDate } from '@klear/date'
import { AgGridReact } from 'ag-grid-react'
import { type ColDef, type GridApi, type GridReadyEvent, type ValueGetterParams } from 'ag-grid-community'
import { klearDateCellYmd } from '@klear/ui/ag-grid/klear-ag-grid-options'
import { useKlearAgGrid } from '@klear/ui/hooks/use-klear-ag-grid'
import { ROUTE_REPORTS } from '@klear/constants'
import { ORDER_EVENT_STATUS_FUNDED } from '@klear/constants/order-event-status'
import { FUNDED_OFFER_STATUSES } from '@klear/invoice-offer/invoice-offer'

import { DEFAULT_CURRENCY_CODE, type CurrencyCode } from '@klear/constants/currency'
import type { InvoiceOfferListLifecycleRead } from '@klear/invoice-offer/invoice-offer-list.wire'
import { Money } from '@klear/money'
import { buildSpvSummaries } from '@klear/invoice-offer/funding-rollup'
import { resolveDateDisplayInput } from '@klear/order-event/list-display'
import EventStatusBadge from '@klear/order-event-ui/event-status-badge'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'
import {
  type DateRangeOption,
  type ReportPageSize,
  type ReportViewSelection,
  defaultReportViewSelection,
  loadHiddenReportColumns,
  loadReportPageSize,
  OrderEventLinkCell,
  OrderLinkCell,
  ReportDateRangeModal,
  exposureReplayEndDate,
  reportViewSummary,
  resolveDateRange,
  resolveExposureAsOfDate
} from '@/app/reports/report-shared'
import { useInvoiceOffers, type InvoiceOfferListFilters } from '@klear/ui/hooks/use-invoice-offers'
import { useCurrencyScopedReport } from '@klear/ui/hooks/use-currency-scoped-report'
import { CurrencyReportSelector } from '@klear/ui/components/currency-report-selector'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

type FundingViewMode = 'funded-in-period' | 'exposure-as-of'

const FUNDING_COLUMN_DEFS: ColDef<InvoiceOfferListLifecycleRead>[] = [
  { colId: 'spv', headerName: 'SPV' },
  { colId: 'orderId', headerName: 'Order ID' },
  { colId: 'offerId', headerName: 'Offer ID' },
  { colId: 'trader', headerName: 'Trader' },
  { colId: 'buyer', headerName: 'Buyer' },
  { colId: 'fundingDate', headerName: 'Funding Date' },
  { colId: 'dueDate', headerName: 'Due Date' },
  { colId: 'completedDate', headerName: 'Completed Date' },
  { colId: 'status', headerName: 'Status' },
  { colId: 'purchasePrice', headerName: 'Purchase Price' },
  { colId: 'currency', headerName: 'Currency' }
]

const HIDDEN_COLS_STORAGE_KEY = 'reports.fundingHiddenColumns.v3'
const PAGE_SIZE_STORAGE_KEY = 'reports.fundingPageSize.v1'
const VIEW_MODE_STORAGE_KEY = 'reports.fundingViewMode.v1'

function loadFundingViewMode(): FundingViewMode {
  if (typeof window === 'undefined') return 'funded-in-period'
  try {
    const raw = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    if (raw === 'funded-in-period' || raw === 'exposure-as-of') return raw
  } catch {}
  return 'funded-in-period'
}

function buildFundingOfferListInput(
  viewSelection: ReportViewSelection,
  viewMode: FundingViewMode
): InvoiceOfferListFilters {
  const isExposure = viewMode === 'exposure-as-of'
  const exposureAsOf = resolveExposureAsOfDate(viewSelection)

  return {
    status: isExposure ? [ORDER_EVENT_STATUS_FUNDED] : [...FUNDED_OFFER_STATUSES],
    skipTraderScope: true,
    enrichLifecycleEffectiveDates: true,
    replayEndDate: isExposure ? exposureReplayEndDate(exposureAsOf) : viewSelection.endDate,
    fundedEffectiveStartDate: !isExposure ? viewSelection.startDate : undefined,
    fundedEffectiveEndDate: !isExposure ? viewSelection.endDate : undefined
  }
}

function spvCardSubtitle(viewMode: FundingViewMode, viewSelection: ReportViewSelection): string {
  if (viewMode === 'exposure-as-of') {
    return `Outstanding on ${resolveExposureAsOfDate(viewSelection)}`
  }
  if (viewSelection.startDate && viewSelection.endDate) {
    return `Funded ${viewSelection.startDate} → ${viewSelection.endDate}`
  }
  return 'Funded (all time)'
}

function offerCurrencyCode(offer: InvoiceOfferListLifecycleRead): CurrencyCode {
  return (offer.currency ?? DEFAULT_CURRENCY_CODE) as CurrencyCode
}

export default function FundingReport() {
  useDocumentTitle('Funding Report')
  const agGrid = useKlearAgGrid()
  const [gridApi, setGridApi] = useState<GridApi<InvoiceOfferListLifecycleRead> | null>(null)
  const [viewSelection, setViewSelection] = useState<ReportViewSelection>(() => defaultReportViewSelection())
  const [viewMode, setViewMode] = useState<FundingViewMode>(() => loadFundingViewMode())
  const [rangeModalOpen, setRangeModalOpen] = useState(false)
  const [dateRangeOption, setDateRangeOption] = useState<DateRangeOption>('current-month')
  const [customStart, setCustomStart] = useState<KlearDate | undefined>(undefined)
  const [customEnd, setCustomEnd] = useState<KlearDate | undefined>(undefined)
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() =>
    loadHiddenReportColumns(HIDDEN_COLS_STORAGE_KEY)
  )
  const [pageSize, setPageSize] = useState<ReportPageSize>(() => loadReportPageSize(PAGE_SIZE_STORAGE_KEY))

  const listInput = useMemo(() => buildFundingOfferListInput(viewSelection, viewMode), [viewSelection, viewMode])

  const { offers: rawOffers, isLoading, isFetching, refetch, error } = useInvoiceOffers(listInput)

  const offers = useMemo((): InvoiceOfferListLifecycleRead[] => {
    return [...rawOffers].sort((a, b) => {
      const aFund = a.fundedEffectiveDate?.toJSON() ?? ''
      const bFund = b.fundedEffectiveDate?.toJSON() ?? ''
      const fundCmp = bFund.localeCompare(aFund)
      if (fundCmp !== 0) return fundCmp
      return b.id - a.id
    })
  }, [rawOffers])

  const offerMonies = useMemo(() => offers.map((offer) => offer.purchasePrice), [offers])

  const { selectedCurrency, setSelectedCurrency, showCurrencySelector, availableCurrencies } = useCurrencyScopedReport([
    offerMonies
  ])

  const filteredOffers = useMemo(
    () => offers.filter((offer) => offerCurrencyCode(offer) === selectedCurrency),
    [offers, selectedCurrency]
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(HIDDEN_COLS_STORAGE_KEY, JSON.stringify([...hiddenColumns]))
    } catch {}
  }, [hiddenColumns])

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, toString(pageSize))
    } catch {}
  }, [pageSize])

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode)
    } catch {}
  }, [viewMode])

  const openRangeModal = () => {
    setDateRangeOption(viewSelection.option)
    if (viewSelection.option === 'date-range') {
      setCustomStart(viewSelection.startDate ? KlearDate.parse(viewSelection.startDate) : undefined)
      setCustomEnd(viewSelection.endDate ? KlearDate.parse(viewSelection.endDate) : undefined)
    }
    setRangeModalOpen(true)
  }

  const rangeModalPrimaryDisabled = dateRangeOption === 'date-range' && (!customStart || !customEnd)

  const applyDateRange = () => {
    const { startDate, endDate } = resolveDateRange(dateRangeOption, customStart, customEnd)
    const startStr = startDate?.toJSON()
    const endStr = endDate?.toJSON()
    if (dateRangeOption !== 'all-time' && (!startStr || !endStr)) return
    setViewSelection({
      option: dateRangeOption,
      startDate: dateRangeOption === 'all-time' ? undefined : startStr,
      endDate: dateRangeOption === 'all-time' ? undefined : endStr
    })
    setRangeModalOpen(false)
  }

  const handleExport = () => {
    if (!gridApi) return
    gridApi.exportDataAsCsv({
      fileName: `funding-${viewMode}-${KlearDate.today().toJSON()}.csv`
    })
  }

  const toggleColumnVisibility = useCallback((colId: string, visible: boolean) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev)
      if (visible) next.delete(colId)
      else next.add(colId)
      return next
    })
  }, [])

  const columnDefs = useMemo<ColDef<InvoiceOfferListLifecycleRead>[]>(() => {
    return FUNDING_COLUMN_DEFS.map((col) => {
      const colId = col.colId!
      const def: ColDef<InvoiceOfferListLifecycleRead> = {
        ...col,
        sortable: true,
        resizable: true,
        hide: hiddenColumns.has(colId)
      }

      switch (colId) {
        case 'spv':
          def.field = 'fundingSpvLabel'
          def.filter = 'agTextColumnFilter'
          break
        case 'orderId':
          def.field = 'orderIdDisplay'
          def.cellRenderer = OrderLinkCell
          def.filter = 'agTextColumnFilter'
          break
        case 'offerId':
          def.valueGetter = (params: ValueGetterParams<InvoiceOfferListLifecycleRead>) =>
            params.data?.idDisplay ?? toString(params.data?.id ?? '')
          def.cellRenderer = OrderEventLinkCell
          def.filter = 'agTextColumnFilter'
          break
        case 'trader':
          def.field = 'traderName'
          def.filter = 'agTextColumnFilter'
          break
        case 'buyer':
          def.field = 'customerName'
          def.filter = 'agTextColumnFilter'
          break
        case 'fundingDate':
          def.type = 'klearDate'
          def.valueGetter = (params: ValueGetterParams<InvoiceOfferListLifecycleRead>) =>
            klearDateCellYmd(params.data?.fundedEffectiveDate)
          break
        case 'dueDate':
          def.type = 'klearDate'
          def.valueGetter = (params: ValueGetterParams<InvoiceOfferListLifecycleRead>) =>
            klearDateCellYmd(resolveDateDisplayInput(params.data?.endDate))
          break
        case 'completedDate':
          def.type = 'klearDate'
          def.valueGetter = (params: ValueGetterParams<InvoiceOfferListLifecycleRead>) =>
            klearDateCellYmd(params.data?.completedEffectiveDate)
          break
        case 'status':
          def.field = 'status'
          def.cellRenderer = (params: { data?: InvoiceOfferListLifecycleRead }) =>
            params.data?.status ? (
              <EventStatusBadge
                eventId={params.data.id}
                orderId={params.data.orderId}
                statusLabel={params.data.statusLabel}
              />
            ) : null
          def.filter = 'agTextColumnFilter'
          break
        case 'purchasePrice':
          def.type = 'money'
          def.valueGetter = (params: ValueGetterParams<InvoiceOfferListLifecycleRead>) =>
            params.data?.purchasePrice ?? null
          break
        case 'currency':
          def.field = 'currency'
          def.filter = 'agTextColumnFilter'
          break
      }

      return def
    })
  }, [hiddenColumns])

  const summary = reportViewSummary(viewSelection)
  const spvSubtitle = spvCardSubtitle(viewMode, viewSelection)
  const exposureOnly = viewMode === 'exposure-as-of'
  const spvSummaries = useMemo(
    () => buildSpvSummaries(filteredOffers, { exposureOnly }),
    [filteredOffers, exposureOnly]
  )

  const allSpvsTotal = useMemo(() => {
    if (spvSummaries.length <= 1) return null
    return {
      offerCount: spvSummaries.reduce((sum, s) => sum + s.offerCount, 0),
      totalPurchasePrice: Money.sum(spvSummaries.map((s) => s.totalPurchasePrice))
    }
  }, [spvSummaries])

  const viewModeLabel = viewMode === 'funded-in-period' ? 'Funded in period' : 'Outstanding exposure'
  const exposureAsOf = resolveExposureAsOfDate(viewSelection)
  const statusContextLabel =
    viewMode === 'exposure-as-of'
      ? exposureReplayEndDate(exposureAsOf) == null
        ? 'Live status (matches SPV utilization); dates from change log'
        : `Status replayed through ${exposureAsOf}; dates from change log`
      : viewSelection.endDate
        ? `Status as of ${viewSelection.endDate}; dates from change log`
        : 'Status current; dates from change log'
  const visibleColumnCount = FUNDING_COLUMN_DEFS.length - hiddenColumns.size

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 px-4 py-4">
      <div className="flex shrink-0 items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/${ROUTE_REPORTS}`}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Reports
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold truncate">Funding report</h1>
        </div>
        <Button
          onClick={handleExport}
          disabled={!gridApi || filteredOffers.length === 0}
          className="inline-flex items-center shrink-0"
        >
          <Download className="h-4 w-4" />
          <span className="ml-2">Export</span>
        </Button>
      </div>

      {error && <p className="text-destructive text-sm shrink-0">{error.message}</p>}

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded border bg-muted/40 px-3 py-2 text-sm">
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          <Select value={viewMode} onValueChange={(v) => setViewMode(v as FundingViewMode)}>
            <SelectTrigger className="h-8 w-[220px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="funded-in-period">Funded in period</SelectItem>
              <SelectItem value="exposure-as-of">Outstanding exposure</SelectItem>
            </SelectContent>
          </Select>
          {showCurrencySelector && (
            <>
              <span className="text-muted-foreground hidden sm:inline">|</span>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm shrink-0">Currency</span>
                <CurrencyReportSelector
                  value={selectedCurrency}
                  onChange={setSelectedCurrency}
                  currencies={availableCurrencies}
                  className="h-8 w-[140px] text-sm"
                />
              </div>
            </>
          )}
          <span className="text-muted-foreground hidden sm:inline">|</span>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-muted-foreground shrink-0">Viewing:</span>
            <span className="font-medium truncate">
              {viewModeLabel}
              {summary ? ` · ${summary}` : ''}
            </span>
            <span className="text-muted-foreground text-xs truncate hidden lg:inline">{statusContextLabel}</span>
            {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Select
            value={toString(pageSize)}
            onValueChange={(v) => setPageSize(v === 'all' ? 'all' : (toNumber(v) as ReportPageSize))}
          >
            <SelectTrigger className="h-8 w-[140px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Show all</SelectItem>
              <SelectItem value="50">50 per page</SelectItem>
              <SelectItem value="100">100 per page</SelectItem>
              <SelectItem value="500">500 per page</SelectItem>
              <SelectItem value="1000">1000 per page</SelectItem>
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" disabled={filteredOffers.length === 0}>
                <Columns3 className="h-3.5 w-3.5 mr-2" />
                Columns
                {hiddenColumns.size > 0 && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({visibleColumnCount}/{FUNDING_COLUMN_DEFS.length})
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[60vh] overflow-y-auto">
              <DropdownMenuLabel className="flex items-center justify-between gap-4">
                <span>Toggle columns</span>
                <button
                  type="button"
                  onClick={() => setHiddenColumns(new Set())}
                  className="text-xs font-normal text-muted-foreground hover:text-foreground"
                  disabled={hiddenColumns.size === 0}
                >
                  Show all
                </button>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {FUNDING_COLUMN_DEFS.map((col) => {
                const colId = col.colId!
                const visible = !hiddenColumns.has(colId)
                return (
                  <DropdownMenuCheckboxItem
                    key={colId}
                    checked={visible}
                    onCheckedChange={(next) => toggleColumnVisibility(colId, Boolean(next))}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {col.headerName}
                  </DropdownMenuCheckboxItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" variant="ghost" onClick={openRangeModal}>
            <Pencil className="h-3.5 w-3.5 mr-2" />
            Change date range
          </Button>
          <Button size="sm" variant="ghost" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? (
              <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {!isLoading && filteredOffers.length > 0 && (
        <div className="shrink-0 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {allSpvsTotal && (
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="p-4 space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  All SPVs · {selectedCurrency}
                </p>
                <p className="text-xl font-semibold tabular-nums">{allSpvsTotal.totalPurchasePrice.toString()}</p>
                <p className="text-xs text-muted-foreground">
                  {allSpvsTotal.offerCount} offer{allSpvsTotal.offerCount === 1 ? '' : 's'} · {spvSubtitle}
                </p>
              </CardContent>
            </Card>
          )}
          {spvSummaries.map((spv) => (
            <Card key={`${spv.organizationId}-${spv.totalPurchasePrice.currency}`}>
              <CardContent className="p-4 space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground truncate">
                  {spv.spvName}
                </p>
                <p className="text-xl font-semibold tabular-nums">{spv.totalPurchasePrice.toString()}</p>
                <p className="text-xs text-muted-foreground">
                  {spv.offerCount} offer{spv.offerCount === 1 ? '' : 's'} · {spvSubtitle}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="flex-1 min-h-0 flex flex-col rounded-md border">
        {isLoading && filteredOffers.length === 0 ? (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading funding data…
          </div>
        ) : (
          <div className="h-full min-h-0 w-full">
            <AgGridReact
              rowHeight={32}
              headerHeight={36}
              rowData={filteredOffers}
              columnDefs={columnDefs}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              animateRows={false}
              pagination={pageSize !== 'all'}
              paginationPageSize={pageSize === 'all' ? undefined : pageSize}
              paginationPageSizeSelector={false}
              theme={agGrid.theme}
              columnTypes={agGrid.columnTypes}
              enableCellTextSelection={true}
              onGridReady={(event: GridReadyEvent<InvoiceOfferListLifecycleRead>) => setGridApi(event.api)}
            />
          </div>
        )}
      </div>

      <ReportDateRangeModal
        open={rangeModalOpen}
        onOpenChange={setRangeModalOpen}
        title="Change date range"
        primaryText="Apply"
        idPrefix="funding"
        dateRangeOption={dateRangeOption}
        onDateRangeOptionChange={setDateRangeOption}
        customStart={customStart}
        customEnd={customEnd}
        onCustomStartChange={setCustomStart}
        onCustomEndChange={setCustomEnd}
        onConfirm={applyDateRange}
        primaryDisabled={rangeModalPrimaryDisabled}
      />
    </div>
  )
}
